import React from 'react';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Truck, 
  Users, 
  BarChart3, 
  Settings, 
  FileText,
  Warehouse,
  ClipboardList,
  Shield,
  Layers,
  Search,
  QrCode,
  X,
  Building,
  User
} from 'lucide-react';

const Sidebar = ({ activeTab, setActiveTab, currentUser, isOpen, onClose }) => {
  const menuItems = [
    { id: 'dashboard', label: 'Operations Dashboard', icon: LayoutDashboard, permissions: ['all', 'assembly', 'inventory_view'] },
    { id: 'kit-assembly', label: 'Manufacturing Operations', icon: Layers, permissions: ['all', 'assembly'] },
    { id: 'component-inventory', label: 'Inventory Management', icon: Package, permissions: ['all', 'inventory_view', 'inventory_edit'] },
    { id: 'batch-traceability', label: 'Regulatory Traceability', icon: Search, permissions: ['all', 'quality_control', 'reports'] },
    { id: 'barcode-integration', label: 'Tracking & Identification', icon: QrCode, permissions: ['all', 'assembly', 'inventory_view'] },
    { id: 'receiving', label: 'Component Receiving', icon: Truck, permissions: ['all', 'inventory_edit'] },
    { id: 'orders', label: 'Order Processing', icon: ShoppingCart, permissions: ['all', 'orders'] },
    { id: 'shipping', label: 'Distribution & Shipping', icon: Truck, permissions: ['all', 'orders'] },
    { id: 'quality', label: 'Quality Assurance', icon: Shield, permissions: ['all', 'quality_control'] },
    { id: 'reports', label: 'Compliance Reporting', icon: BarChart3, permissions: ['all', 'reports'] },
    { id: 'users', label: 'User Administration', icon: Users, permissions: ['all'] },
    { id: 'settings', label: 'System Configuration', icon: Settings, permissions: ['all'] },
  ];

  const hasPermission = (item) => {
    if (!currentUser || !currentUser.permissions) return false;
    if (currentUser.permissions.includes('all')) return true;
    return item.permissions.some(permission => currentUser.permissions.includes(permission));
  };

  const handleItemClick = (itemId) => {
    setActiveTab(itemId);
    // Close sidebar on mobile after selection
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-40 z-30"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed left-0 top-0 pt-20 h-screen bg-white border-r-2 border-gray-200 z-20 transition-transform duration-300 ease-in-out shadow-lg
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-72 lg:w-72 overflow-y-auto
      `}>
        <div className="p-6">
          {/* Mobile Close Button */}
          <div className="lg:hidden flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">System Navigation</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 bg-gray-100 rounded-md"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Desktop Header */}
          <div className="hidden lg:block mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center mb-2">
              <Building className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-800">Manufacturing Control System</h2>
            </div>
            <p className="text-sm text-gray-600">Health Canada Compliant Operations</p>
          </div>
          
          <nav>
            <ul className="space-y-1">
              {menuItems.filter(hasPermission).map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleItemClick(item.id)}
                      className={`w-full flex items-center px-4 py-3 text-left rounded-md transition-all duration-200 border ${
                        activeTab === item.id
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                          : 'text-gray-700 hover:bg-blue-50 hover:border-blue-200 border-transparent'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Info - Professional */}
          <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-gray-50 rounded-lg border border-blue-200">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center border-2 border-blue-300 mr-3">
                <User className="h-5 w-5 text-blue-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{currentUser?.name}</div>
                <div className="text-xs text-blue-600">{currentUser?.role}</div>
              </div>
            </div>
            <div className="text-xs text-gray-600 bg-white px-2 py-1 rounded border">
              Session: Active | System: Operational
            </div>
          </div>

          {/* Compliance Notice */}
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center mb-1">
              <Shield className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-xs font-semibold text-green-800">Compliance Status</span>
            </div>
            <div className="text-xs text-green-700">Health Canada Certified System</div>
            <div className="text-xs text-green-600">All operations monitored & logged</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;